import type {LoaderFunction, MetaFunction} from '@remix-run/node'
import {useLoaderData, useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import type {SyntheticEvent} from 'react'
import React, {useMemo, useState} from 'react'
import {slugToText} from '@roundforest/text-transforms-commons'
import clsx from 'clsx'
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react'
import {setEnvironmentWith} from '~/utils/async-storage.server'
import CustomerHighlightsBeyond from '~/components/commons/customer-insights-beyond'
import {DealsWomanIcon} from '~/icons/product-page/deals-woman'
import {
  fetchProductPageStoreDataBySlugAndProductSlug,
  fetchProductPageStoreFakeDataBySlugAndProductSlug,
} from '~/api/product-features-page-data-fetcher.server'
import type {
  AffiliateLink,
  BuyingOptionsSortOption,
  ProductStoreDataLoaderResponse,
  RootAndListContext,
  RootLoaderResponse,
} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {objectToQueryString} from '~/utils/commons'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {SortingArrowsIcon} from '~/icons/product-page/sorting-arrows-icon'
import {IconChevronRight} from '~/icons/brg-icon-chevron-right'
import {sortBuyingOptions} from '~/utils/products-utils'
import {RecommendedProducts} from './recommended-products/recommended-products'
import ImageGallery from './image-gallery/image-gallery'
import Breadcrumbs from './breadcrumbs/breadcrumbs'
import Variations from './variations'
import KeyFeatures from './key-features'
import BuyingOptions from './buying-options'

const BUYING_OPTIONS_SORT_OPTIONS: {value: BuyingOptionsSortOption; labelKey: 'price' | 'recommended'}[] = [
  {value: 'price', labelKey: 'price'},
  {value: 'recommended', labelKey: 'recommended'},
]

export const meta: MetaFunction<typeof loader> = ({data}: {data: ProductStoreDataLoaderResponse}) => {
  const locale = data?.locale?.toLowerCase() ?? 'us'
  const listSlug = data?.slug
  const domain = data?.domain
  const {camelCaseUrl, description} = getDomainConfig(domain, locale)
  const productSlug = data?.productSlug
  const productImage = data?.storeData?.productImages?.[0]?.small
  const productName = slugToText(productSlug?.slice(0, productSlug?.lastIndexOf('-')))
  const metaTitle = `${productName} Review | ${camelCaseUrl}`
  const metaDescription = description(productName, '')
  return [
    {
      title: metaTitle,
    },
    {
      name: 'description',
      content: metaDescription,
    },
    {
      name: 'viewport',
      content: 'width=device-width,initial-scale=1',
    },
    {
      name: 'charset',
      content: 'utf-8',
    },
    {
      property: 'og:url',
      content: `https://${domain}/pp/${listSlug}/${productSlug}`,
    },
    {
      property: 'og:locale',
      content: data?.locale?.toUpperCase(),
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '1024',
    },
    {
      property: 'og:image',
      content: productImage,
    },
    {
      property: 'og:image:alt',
      content: 'Product Image',
    },
    {
      property: 'og:title',
      content: metaTitle,
    },
    {
      property: 'og:description',
      content: metaDescription,
    },
    {
      property: 'og:type',
      content: 'website',
    },
  ]
}

export const loader: LoaderFunction = async ({context, params}) => {
  setEnvironmentWith(context)
  const {locale, domain} = context
  const slug = params?.slug || ''
  const productSlug = params?.productSlug || ''

  try {
    const ppData =
      process.env.NODE_ENV === 'development'
        ? await fetchProductPageStoreFakeDataBySlugAndProductSlug()
        : await fetchProductPageStoreDataBySlugAndProductSlug({
            slug,
            productSlug,
            imageProxyCredentials: {host: process.env.IMAGE_PROXY_HOST!, key: process.env.IMAGE_PROXY_KEY!},
          })
    return new Response(JSON.stringify({...ppData, slug, productSlug, locale, domain}), {
      headers: {
        'Content-Type': 'application/json',
      },
      status: 200,
    })
  } catch (_error) {
    throw new Response('Not Found', {status: 404})
  }
}
const ProductPage = () => {
  const {language, domain, locale, featureFlags, trackAnalytics, traffic} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const {
    storeData,
    breadcrumbs,
    otherProducts,
    selectedProduct,
    selectedProductPosition,
    listScore,
    productSlug,
    slug,
  } = useLoaderData() as ProductStoreDataLoaderResponse
  const {styles} = getDomainConfig(domain, locale)

  const translations = getTranslation(language)
  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickoutCustom()
  const {clickId, setShowCookieConsentGdpr} = useOutletContext<RootAndListContext>()
  const {buyingOptions, productImages, variations, productTitle, productTitleShort, rating} = storeData
  const {keyFeatures, affiliateLink, asin, productBrief} = selectedProduct || {}
  const {categorizedQuotes} = productBrief || {}
  const submit = useSubmit()
  const [sortBy, setSortBy] = useState<BuyingOptionsSortOption | null>(null)
  const sortedBuyingOptions = useMemo(() => sortBuyingOptions(buyingOptions || [], sortBy), [buyingOptions, sortBy])

  const handleSortChange = (value: BuyingOptionsSortOption) => {
    setSortBy(value)
  }

  const [ratingRef] = useReportWidgetSeen({
    widget_name: 'css-read-more-reviews',
    widget_place: 'top',
  })
  const [sortingRef] = useReportWidgetSeen({
    widget_name: 'css-sorting',
    widget_place: 'top',
  })
  const [sortingOptionsRef] = useReportWidgetSeen({
    widget_name: 'css-sorting-options',
    widget_place: 'top',
  })
  const currentSortLabel = sortBy
    ? translations.ProductPage[BUYING_OPTIONS_SORT_OPTIONS.find((o) => o.value === sortBy)?.labelKey ?? 'recommended']
    : translations.ProductPage.sortBy

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })
  const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const handleCardClick = (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkAndClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout({
      index,
      item_list_id: productSlug,
      click_element_name: elementName,
      partner_item_brand: itemName,
      partner_item_id: asin || '',
      item_name: itemName,
      item_brand: itemName,
      click_id: affiliateLink?.clickId,
      affiliation: affiliateLink?.type,
      is_dws: false,
      location_id: locale,
      traffic: traffic ?? 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkAndClickId,
    })
    reportWidgetClick({
      widget_name: elementName,
      widget_place: elementPlace,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <div className="flex flex-col gap-8 py-6 mobile:gap-3 mobile:py-3">
      <Breadcrumbs breadcrumbs={breadcrumbs} productTitle={productTitleShort} />
      <div className="content-container mx-auto flex flex-row justify-between gap-5 tablet:flex-col tablet:gap-0">
        <ImageGallery
          images={productImages || []}
          productTitle={productTitleShort}
          className="flex tablet:hidden"
          handleCardClick={handleCardClick}
          shouldForceTrackAnalytics={shouldForceTrackAnalytics}
          trackSearchParams={trackSearchParams}
          clickId={clickId}
          affiliateLink={affiliateLink}
        />
        <div
          className={clsx(
            'flex w-full flex-col overflow-hidden tablet:items-start tablet:rounded-xl tablet:border tablet:p-3',
            styles.CARD_BORDER,
          )}
        >
          {selectedProductPosition ? (
            <div className="w-fit rounded-lg border border-[#0369A1] bg-[#E0F2FE] px-3 py-1.5 text-xs font-medium text-[#0369A1] mobile:py-1">
              {translations.ProductPage.bestRated(selectedProductPosition, slugToText(slug || ''))}
            </div>
          ) : null}

          <div className="flex flex-col tablet:flex-col-reverse">
            {rating ? (
              <div className="mt-3 flex w-fit items-center gap-1.5 tablet:mt-0" ref={ratingRef}>
                <span className="text-xl text-[#FF9900] mobile:text-lg">★</span>
                <span className="border-r-[2px] border-[#A3A3A3] pr-1.5 text-sm leading-4 text-[#525252]">
                  {rating}
                </span>
                <a
                  className="text-sm text-[#0284C7] underline underline-offset-2"
                  href={redirectUrl}
                  onClick={(e) =>
                    handleCardClick(
                      e,
                      0,
                      affiliateLink,
                      affiliateLinkWithClickId,
                      productTitleShort,
                      redirectUrl,
                      'css-read-more-reviews',
                      'top',
                    )
                  }
                  aria-label="read more reviews"
                  target="_blank"
                  rel="noreferrer"
                >
                  {translations.ProductPage.readMoreReviews}
                </a>
              </div>
            ) : null}

            <h1 className="w-fit text-3xl font-bold text-gray-900 tablet:mt-1 tablet:line-clamp-1 tablet:max-w-[305px] tablet:text-2xl">
              {storeData.productTitleShort}
            </h1>
          </div>

          <p className="mt-1 w-fit text-gray-600 tablet:hidden">{translations.ProductPage.basedOn}</p>

          <ImageGallery
            images={productImages || []}
            productTitle={productTitle}
            className="mt-4 hidden tablet:flex tablet:w-full mobile:mt-0"
            handleCardClick={handleCardClick}
            shouldForceTrackAnalytics={shouldForceTrackAnalytics}
            trackSearchParams={trackSearchParams}
            clickId={clickId}
            affiliateLink={affiliateLink}
          />

          <Variations
            variations={variations}
            slug={slug}
            handleCardClick={handleCardClick}
            shouldForceTrackAnalytics={shouldForceTrackAnalytics}
            trackSearchParams={trackSearchParams}
            clickId={clickId}
          />

          <div className="mt-4 tablet:hidden tablet:w-full">
            <h3 className="mb-2 text-sm font-light text-gray-900 tablet:hidden">
              {translations.ProductPage.seeDealsOn}
            </h3>
            <BuyingOptions
              type="main-cta"
              buyingOptions={buyingOptions?.slice(0, 1)}
              productName={productTitleShort}
              className="block w-full gap-2 tablet:justify-center mobile:justify-start"
              handleCardClick={handleCardClick}
              shouldForceTrackAnalytics={shouldForceTrackAnalytics}
              trackSearchParams={trackSearchParams}
              clickId={clickId}
            />
          </div>
        </div>

        <div className="mt-4 hidden tablet:mt-0 tablet:block tablet:w-full">
          <h3 className="mb-2 text-sm font-light text-gray-900 tablet:hidden">{translations.ProductPage.seeDealsOn}</h3>
          <BuyingOptions
            type="main-cta"
            buyingOptions={buyingOptions?.slice(0, 1)}
            productName={productTitleShort}
            className="block w-full gap-2 tablet:justify-center mobile:justify-start"
            handleCardClick={handleCardClick}
            shouldForceTrackAnalytics={shouldForceTrackAnalytics}
            trackSearchParams={trackSearchParams}
            clickId={clickId}
          />
        </div>
        {selectedProduct ? (
          <KeyFeatures
            className="hidden tablet:block"
            keyFeatures={keyFeatures}
            affiliateLink={affiliateLink}
            asin={asin}
            slug={slug}
            productTitleShort={productTitleShort}
            handleCardClick={handleCardClick}
            shouldForceTrackAnalytics={shouldForceTrackAnalytics}
            trackSearchParams={trackSearchParams}
            clickId={clickId}
          />
        ) : null}
      </div>
      {selectedProduct ? (
        <KeyFeatures
          className="my-3 tablet:hidden"
          handleCardClick={handleCardClick}
          shouldForceTrackAnalytics={shouldForceTrackAnalytics}
          trackSearchParams={trackSearchParams}
          clickId={clickId}
          keyFeatures={keyFeatures}
          affiliateLink={affiliateLink}
          asin={asin}
          slug={slug}
          productTitleShort={productTitleShort}
        />
      ) : null}
      <div className="bg-[#FAFAFA] py-10 tablet:bg-[#F5F5F5] tablet:py-4">
        <div className="content-container mx-auto flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between">
            <h3 className="text-xl font-semibold text-[#030303]">{translations.ProductPage.buyingOptions}</h3>
            <Menu>
              <MenuButton
                ref={sortingRef}
                onClick={() => {
                  reportWidgetClick({
                    widget_name: 'css-sorting',
                    widget_place: 'top',
                  })
                }}
                className="flex h-8 flex-row items-center gap-1 rounded-md bg-[#E2E8F0] px-3 text-sm font-light outline-none [&>.chevron]:rotate-90 [&>.chevron]:data-[open]:-rotate-90"
              >
                {!sortBy ? <SortingArrowsIcon /> : null}
                {currentSortLabel}
                {sortBy ? <IconChevronRight className="chevron transition duration-200 ease-out" /> : null}
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                ref={sortingOptionsRef}
                className="z-20 mt-2 flex w-48 flex-col divide-y-1 divide-black rounded-md border border-solid bg-white text-base font-light shadow-lg outline-none *:py-5"
              >
                {BUYING_OPTIONS_SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value}>
                    <button
                      type="button"
                      onClick={() => {
                        handleSortChange(option.value)
                        reportWidgetClick({
                          widget_name: 'css-sorting-options',
                          widget_place: 'top',
                          trigger_type: option.value,
                        })
                      }}
                    >
                      {translations.ProductPage[option.labelKey]}
                    </button>
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
          </div>
          <BuyingOptions
            className="flex w-full flex-col gap-3"
            type="options-cta"
            buyingOptions={sortedBuyingOptions}
            productName={productTitleShort}
            handleCardClick={handleCardClick}
            shouldForceTrackAnalytics={shouldForceTrackAnalytics}
            trackSearchParams={trackSearchParams}
            clickId={clickId}
          />
        </div>
      </div>

      {selectedProduct ? (
        <CustomerHighlightsBeyond
          className="content-container mx-auto"
          redirectUrl={affiliateLink?.url}
          handleCardClick={() => {}}
          showBottomCta={false}
          cardPlacement={1}
          ratingCount={storeData?.reviewCount}
          score={listScore?.toString()}
          chosenBy={undefined}
          customerHighlights={categorizedQuotes}
        />
      ) : null}

      <RecommendedProducts
        products={otherProducts}
        handleCardClick={handleCardClick}
        shouldForceTrackAnalytics={shouldForceTrackAnalytics}
        trackSearchParams={trackSearchParams}
        clickId={clickId}
      />

      <DealsWomanIcon className="mx-auto my-14" />
    </div>
  )
}

export default ProductPage
